<template>
  <v-dialog v-model="dialog" width="1024px">
    <template #activator="{ on }">
      <v-btn :color="color" outlindex v-on="on">
        <v-icon left>map</v-icon> {{ label }}
      </v-btn>
    </template>
    <div class="dialog-container">
      <map-component @created="onMapCreated" />

      <div v-if="geometries && geometries.length" class="ready-container">
        <span>Создано элементов: {{ geometries.length }}</span>
        <v-btn color="primary" class="mt-2" small @click="onReady">Готово</v-btn>
      </div>

      <import-file-dialog
        v-if="isImportDialogOpen"
        :is-dialog-open="isImportDialogOpen"
        @closed="toggleImportDialog"
        @add-import-file="addImportFile"
      />
    </div>
  </v-dialog>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import turfBbox from '@turf/bbox';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import MapComponent from '@/components/map';
import MapboxButtonsControl from '@/utils/map-box/MapboxButtonsControl';
import ImportFileDialog from '@/components/dialogs/ImportFileDialog.vue';
import { mapActions } from 'vuex';
import { geometryCollection } from '@turf/turf';
import getGeometryType from '@/utils/map-box/get-geometry-type';

export default {
  name: 'map-polygon-editor-dialog',
  components: { MapComponent, ImportFileDialog },
  props: {
    mapType: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Указать область в редакторе',
    },
    color: String,
    geometry: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    dialog: false,
    isImportDialogOpen: false,
    geometries: [],
    importGeometryType: '',
  }),
  computed: {
    type() {
      if (this.geometry) return getGeometryType(this.geometry);
      if (this.mapType === 'polygon') return 'Polygon';
      if (this.mapType === 'linestring') return 'LineString';
      return '';
    },

    isPolygonType() {
      return ['Polygon', 'MultiPolygon'].includes(this.type);
    },

    isLineStringType() {
      return ['LineString', 'MultiLineString'].includes(this.type);
    },

    validGeometry() {
      if (this.isPolygonType) return ['Polygon', 'MultiPolygon'];
      if (this.isLineStringType) return ['LineString', 'MultiLineString'];
      return [];
    },
  },
  methods: {
    ...mapActions(['showNotify']),

    onMapCreated(instance) {
      this.instance = instance;

      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: this.isPolygonType,
          line_string: this.isLineStringType,
          trash: true,
        },
      });

      instance.addControl(this.draw);

      const importButton = new MapboxButtonsControl({
        title: 'Импорт геометрии',
        iconName: 'cloud_upload',
        clickHandler: this.toggleImportDialog,
      });
      instance.addControl(importButton, 'top-right');

      if (this.geometry) {
        this.addGeometryOnMap(this.geometry);
      }

      instance.on('draw.create', this.onDrawEvent);
      instance.on('draw.update', this.onDrawEvent);
      instance.on('draw.delete', this.onDrawEvent);
    },

    addGeometryOnMap(geometry) {
      let bbox = null;
      const addGeometry = (item) => {
        this.draw.add(item);
        this.geometries.push(item);
      };

      if (this.importGeometryType) {
        this.draw.deleteAll();
        this.geometries = [];
      }

      if (geometry.geometries) {
        geometry.geometries.forEach(addGeometry);
        bbox = turfBbox(geometry.geometries[0]);
      } else {
        addGeometry(geometry);
        bbox = turfBbox(geometry);
      }
      if (bbox) this.instance.fitBounds(bbox, { padding: 100 });
    },

    toggleImportDialog() {
      this.isImportDialogOpen = !this.isImportDialogOpen;
    },

    addImportFile(file) {
      try {
        let geometry;
        switch (file.type) {
          case 'Feature':
            ({ geometry } = file);
            break;
          case 'FeatureCollection':
            if (file.features.length) geometry = file;
            break;
          case 'GeometryCollection':
            if (file.geometries.length) geometry = file;
            break;
          case 'Polygon':
          case 'LineString':
          case 'MultiPolygon':
          case 'MultiLineString':
            geometry = file;
            break;
          default:
            throw new Error('Неверный формат файла');
        }

        this.importGeometryType = getGeometryType(file);

        if (this.validGeometry.includes(this.importGeometryType)) {
          this.addGeometryOnMap(geometry);
          this.showNotify({ message: 'Геометрия добавлена' });
          this.toggleImportDialog();
        } else {
          throw new Error(`${this.importGeometryType} - неверный тип координат для геометрии типа ${this.type}. Проверьте файл.`);
        }
      } catch (error) {
        this.showNotify({ type: 'error', message: error.message });
        console.error(error);
      }
    },

    onDrawEvent() {
      this.importGeometryType = '';
      this.geometries = [];

      const all = this.draw.getAll();
      const { features } = all;

      this.geometries = features.map(item => item.geometry);
      this.$forceUpdate();
    },

    onReady() {
      let geometry = null;

      if (this.geometries.length > 1) {
        ({ geometry } = geometryCollection(this.geometries));
      } else {
        ([geometry] = this.geometries);
      }

      this.$emit('change', { geometry });
      this.dialog = false;
    },
  },
};
</script>

<style lang="css" src="@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css"></style>

<style lang="scss" scoped>

.dialog-container {
  display: flex;
  justify-content: center;
  position: relative;
  height: 450px;
  width: 100%;
}

.ready-container {
  position: absolute;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  z-index: 1000;
  bottom: 0;
  padding: 10px;
  background-color: rgb(0, 0, 0, .3);
  color: white;
  width: 240px;
}
</style>
